// The board: holds the filters, sort and open row, keeps them in the URL, and derives everything
// else from the payload.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LeadershipNextStep, Opportunity, PipelinePayload } from '../api/types.ts';
import type { TokenGetter } from '../api/client.ts';
import { withLeadershipSaves } from '../board/leadershipNextStep.ts';
import { readState, writeState } from '../board/url.ts';
import {
  buildFunnel, clearTableFilters, emptyTabMessage, nextSort, openTabFor, personName, repOptions, scopeRows, sortRows,
  tabCounts, tabHasStages, tabPipelines, viewRows,
} from '../board/view.ts';
import type { Filters, Sort, SortKey } from '../board/view.ts';
import { ActiveFilters, FilterMenu, RepPicker, SearchBox, Tabs } from './Controls.tsx';
import { Masthead, StaleDataBanner } from './Masthead.tsx';
import { Drawer } from './Drawer.tsx';
import { PipelineTable } from './PipelineTable.tsx';
import { StageFunnel } from './StageFunnel.tsx';

export function Board({
  payload, today, getToken,
}: {
  payload: PipelinePayload;
  today: string;
  getToken: TokenGetter;
}) {
  const initial = useMemo(() => readState(window.location.search), []);
  const [filters, setFilters] = useState<Filters>(initial.filters);
  const [sort, setSort] = useState<Sort | null>(initial.sort);
  const [openId, setOpenId] = useState<string | null>(initial.open_id);

  // Leadership next steps saved since load (GRO-238), laid over the payload so the table's Next
  // step column shows a save straight away, not on the next reload.
  const [leadershipSaves, setLeadershipSaves] = useState<ReadonlyMap<string, LeadershipNextStep | null>>(new Map());
  const opportunities = useMemo(
    () => withLeadershipSaves(payload.opportunities, leadershipSaves), [payload.opportunities, leadershipSaves],
  );
  const onLeadershipSaved = useCallback((oppId: string, value: LeadershipNextStep | null) => {
    setLeadershipSaves(current => new Map(current).set(oppId, value));
  }, []);
  const instanceUrl = payload.salesforce_instance_url ?? null;

  // The address bar follows the board, so a link opens the same view. Replaced, not pushed:
  // every filter click shouldn't be a Back step.
  useEffect(() => {
    const search = writeState({ filters, sort, open_id: openId });
    if (search !== window.location.search) {
      window.history.replaceState(null, '', window.location.pathname + search);
    }
  }, [filters, sort, openId]);

  // Scope (the tab's pipelines and the rep) feeds the stage boxes.
  const pipelines = useMemo(() => tabPipelines(filters.tab), [filters.tab]);
  const scoped = useMemo(() => scopeRows(opportunities, filters), [opportunities, filters]);
  // The boxes count the rep's rows but take their names, and on renewals which boxes exist, from
  // the whole pipeline.
  const pipelineRows = useMemo(() => scopeRows(opportunities, { ...filters, rep_id: null }), [opportunities, filters]);
  const funnel = useMemo(() => buildFunnel(scoped, pipelineRows, pipelines), [scoped, pipelineRows, pipelines]);
  const counts = useMemo(() => tabCounts(opportunities, filters), [opportunities, filters]);
  const reps = useMemo(() => repOptions(opportunities, filters), [opportunities, filters]);
  const repName = filters.rep_id ? personName(opportunities, filters.rep_id) : null;

  // The table's rows, and the tab's rows before any table filter. Search is a table filter: it
  // narrows the tab, and All is where to search everything.
  const rows = useMemo(() => sortRows(viewRows(opportunities, filters), sort), [opportunities, filters, sort]);
  const tabRows = useMemo(() => viewRows(opportunities, clearTableFilters(filters)), [opportunities, filters]);

  const openOpportunity = opportunities.find(opportunity => opportunity.opp_id === openId) ?? null;

  const onSort = (key: SortKey) => setSort(current => nextSort(current, key));
  const onOpen = (opportunity: Opportunity) => setOpenId(opportunity.opp_id);
  const onClose = useCallback(() => setOpenId(null), []);
  const onClear = () => setFilters(clearTableFilters(filters));

  // A stage box shows that stage on the open tab or All (New Business from a closed tab); a second
  // click clears it.
  const toggleStage = (stageNumber: number) => {
    const tab = openTabFor(filters.tab);
    setFilters({ ...filters, tab, stage: filters.stage === stageNumber && tab === filters.tab ? null : stageNumber });
  };
  const toggleRep = (repId: string) => setFilters({
    ...filters, rep_id: filters.rep_id === repId ? null : repId,
  });
  const onShowEveryone = () => setFilters({ ...filters, rep_id: null });

  // An empty table says why: the tab holds nothing for this rep (or nothing at all), or the
  // table's filters narrowed it to nothing.
  const tabIsEmpty = tabRows.length === 0;
  const empty = tabIsEmpty ? (
    <span className="empty">
      {emptyTabMessage(filters.tab, filters.rep_id ? repName ?? 'this rep' : null)}
      {filters.rep_id ? <button type="button" className="button" onClick={onShowEveryone}>Show everyone</button> : null}
    </span>
  ) : (
    <span className="empty">
      Nothing matches these filters.
      <button type="button" className="button" onClick={onClear}>Clear filters</button>
    </span>
  );

  return (
    <>
      <Masthead salesforceThrough={payload.salesforce_through} />
      <StaleDataBanner salesforceThrough={payload.salesforce_through} />

      {/* Every tab has the stage boxes, for consistency: on All they count every open deal. */}
      <StageFunnel
        funnel={funnel}
        selectedStage={tabHasStages(filters.tab) ? filters.stage : null}
        onSelect={toggleStage}
      />

      <section className="table-section" aria-label="Opportunities">
        <Tabs filters={filters} counts={counts} onChange={setFilters} />
        <div className="table-controls">
          <RepPicker filters={filters} reps={reps} selectedName={repName} onChange={setFilters} />
          <FilterMenu filters={filters} funnel={funnel} tabRows={tabRows} onChange={setFilters} onClear={onClear} />
          <SearchBox filters={filters} onChange={setFilters} />
          <ActiveFilters filters={filters} funnel={funnel} onChange={setFilters} onClear={onClear} />
          <span className="row-count num">
            {rows.length === tabRows.length ? `${rows.length} shown` : `${rows.length} of ${tabRows.length}`}
          </span>
        </div>
        <PipelineTable
          rows={rows}
          today={today}
          instanceUrl={instanceUrl}
          sort={sort}
          currentTab={filters.tab}
          selectedId={openId}
          empty={empty}
          getToken={getToken}
          onSort={onSort}
          onSortChange={setSort}
          onOpen={onOpen}
          onRep={toggleRep}
          onLeadershipSaved={onLeadershipSaved}
        />
      </section>

      {openOpportunity ? (
        <Drawer
          opportunity={openOpportunity}
          today={today}
          instanceUrl={instanceUrl}
          getToken={getToken}
          onClose={onClose}
          onLeadershipSaved={onLeadershipSaved}
        />
      ) : null}
    </>
  );
}
